import { cn } from "@/lib/utils";
import { union } from "lodash";
import type * as React from "react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  ErrorIcon,
  ExportFailedIcon,
  ExportedIcon,
  ReviewedIcon,
  ToReviewIcon,
  WaitingForClientApprovalIcon,
} from "@/components/shared/status/invoice";
import { MultiSelect, type OptionValue } from "@/components/ui/multi-select";
import { useShouldWaitForClientApproval } from "@/hooks/useShouldWaitForClientApproval";
import {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";

type Options = {
  value: Record<string, string>;
  icon: (props: React.HTMLAttributes<SVGSVGElement>) => React.ReactNode;
};

const useStatusOptions = () => {
  const shouldWaitForClientApproval = useShouldWaitForClientApproval();

  const options = useMemo(
    () => [
      {
        value: { status: InvoiceStatus.PREPROCESSED },
        icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
          <ToReviewIcon {...props} />
        ),
      },
      {
        value: { status: InvoiceStatus.REVIEWED },
        icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
          <ReviewedIcon {...props} />
        ),
      },
      ...(shouldWaitForClientApproval
        ? [
            {
              value: {
                status: InvoiceStatus.REVIEWED,
                smbReviewStatus: InvoiceSmbReviewStatus.UNREVIEWED,
              },
              icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
                <WaitingForClientApprovalIcon {...props} />
              ),
            },
          ]
        : []),
      {
        value: { status: InvoiceStatus.EXPORTED },
        icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
          <ExportedIcon {...props} />
        ),
      },
      {
        value: { status: InvoiceStatus.EXPORT_FAILED },
        icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
          <ExportFailedIcon {...props} />
        ),
      },
      {
        value: { status: InvoiceStatus.ERROR },
        icon: (props: React.HTMLAttributes<SVGSVGElement>) => (
          <ErrorIcon {...props} />
        ),
      },
    ],
    [shouldWaitForClientApproval],
  );

  return options as Options[];
};

const getUniqueInputNames = (options: Options[]) => {
  const inputNames = options.reduce((acc, current) => {
    const values = Object.keys(current.value);
    return union(acc, values);
  }, [] as string[]);

  return inputNames;
};

export const StatusInputFilter = () => {
  const { t } = useTranslation();
  const _options = useStatusOptions();

  const { register, setValue, watch } = useFormContext();

  const inputNames = getUniqueInputNames(_options);

  const options = _options.map((option) => ({
    value: option.value,
    label: "",
    icon: (({ className }: { className?: string }) =>
      option.icon({ className })) as React.ComponentType<{
      className?: string;
    }>,
  }));

  const handleValueChange = (_values: OptionValue[] | null) => {
    const values = _values || [];
    const groupedByKeys = values.reduce(
      (acc, current) => {
        if (typeof current === "object" && current !== null) {
          Object.entries(current).forEach(([key, value]) => {
            if (!acc[key]) {
              acc[key] = [];
            }

            if (!acc[key].includes(value)) {
              acc[key].push(value);
            }
          });
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );

    // Clear all input fields first, then set the new values
    inputNames.forEach((inputName) => {
      setValue(inputName, groupedByKeys[inputName] || [], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const values = watch();

  const selectedValues = useMemo(() => {
    const formValues = Object.entries(values).filter(
      ([key, value]) =>
        inputNames.includes(key) && Array.isArray(value) && value.length > 0,
    );

    if (formValues.length === 0) return [];

    return _options
      .map((option) => option.value)
      .filter((optionValue) => {
        return Object.entries(optionValue).every(([key, value]) => {
          const formValue = values[key];
          return Array.isArray(formValue) && formValue.includes(value);
        });
      });
  }, [values, inputNames, _options]);

  return (
    <>
      {inputNames.map((inputName) => (
        <input key={inputName} {...register(inputName)} type="hidden" />
      ))}
      <MultiSelect
        options={options}
        mode="multiple"
        defaultValue={selectedValues}
        onValueChange={handleValueChange}
        placeholder={t("invoices-table.filters.status.placeholder")}
        className={cn(
          selectedValues.length > 0 ? "bg-secondary" : "text-muted-foreground",
        )}
        enableClear
      />
    </>
  );
};
